import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { resolveOpenPlacementPets, type FosterHome, type FosterPlacementRecord } from '../lib/fosterAdapters';
import type { RescueDeviceItemPetGql } from '../lib/rescueQueries';
import { trackFosterRosterViewed, trackPetViewed } from '../analytics';
import useAllDeviceItems from './useAllDeviceItems';
import useFosterHomes from './useFosterHomes';

interface UseFosterRosterOptions {
  /**
   * Whether to fetch the roster at all. It backs the assign-to-foster picker as well as the roster
   * section, so it stays on for tabs that only need the picker.
   */
  enabled: boolean;
  /** Whether the roster section is the tab on screen, for the once-per-visit analytics event. */
  visible: boolean;
}

/**
 * The Fosters tab's roster half: the saved homes, the details overlay, and the pet behind a
 * placement. Extracted from the Devices page, which is long enough that this sitting inline made the
 * roster's own coupling — a held home id against a modal's reset, an overlay against a URL change —
 * hard to see.
 */
export default function useFosterRoster({ enabled, visible }: UseFosterRosterOptions) {
  const homes = useFosterHomes({ skip: !enabled });

  // Held by id, not value: every mutation refetches the roster, and a captured object would keep
  // showing stale placements, readiness pills and archive-ability after that.
  const [detailHomeId, setDetailHomeId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [placementPet, setPlacementPet] = useState<RescueDeviceItemPetGql | null>(null);
  const detailHome = homes.homes.find((home) => home.id === detailHomeId) ?? null;

  /**
   * Drained while the tab is up, not while the overlay is open.
   *
   * Gating it on the overlay looks like the cheaper choice and isn't: `skip` discards Apollo's data,
   * so every open re-races the fetch, and a placement whose pet hasn't arrived yet is indistinguishable
   * from one whose pet can't be resolved — the row just renders as plain text with nothing to say so.
   * Losing the link is worse than the fetch, and the fetch is smaller than it looks: this tab is
   * already querying IN_FOSTER for its own table, so this is the same data unpaginated rather than a
   * speculative extra.
   */
  const fosterPetItems = useAllDeviceItems('IN_FOSTER', { skip: !visible });

  /** See {@link resolveOpenPlacementPets} for the matching rules. */
  const placementPets = useMemo(
    () =>
      detailHome
        ? resolveOpenPlacementPets(
            detailHome,
            fosterPetItems.items.map((item) => item.pet),
          )
        : new Map<string, RescueDeviceItemPetGql>(),
    [detailHome, fosterPetItems.items],
  );

  const viewablePlacementIds = useMemo(() => new Set(placementPets.keys()), [placementPets]);

  /**
   * Once per visit, and only once the roster is an answer. Keyed on `loaded` rather than `!loading`
   * and guarded on `error`: both a skipped and a failed query report not-loading with no homes, and
   * reporting those as `home_count: 0` would mix "this rescue has no saved homes" into the same
   * number as "we never found out" — which is the metric sizing the roster.
   *
   * The ref resets on leaving so a return visit counts again, while the refetch every foster
   * mutation triggers does not. A failed load leaves it unset, so a successful retry still counts.
   */
  const viewTracked = useRef(false);
  useEffect(() => {
    if (!visible) {
      viewTracked.current = false;
      return;
    }
    if (!homes.loaded || homes.error || viewTracked.current) {
      return;
    }
    viewTracked.current = true;
    trackFosterRosterViewed(homes.homes.length);
  }, [visible, homes.loaded, homes.error, homes.homes.length]);

  function openDetail(home: FosterHome) {
    setDetailHomeId(home.id);
    setDetailOpen(true);
  }

  // Stable, so callers can name it as an effect dependency rather than suppressing the lint rule.
  const closeDetail = useCallback(() => {
    setDetailOpen(false);
    setDetailHomeId(null);
  }, []);

  /**
   * Opens over the details modal rather than replacing it, so dismissing the pet lands back on the
   * home its placement belongs to.
   */
  function viewPlacementPet(placement: FosterPlacementRecord) {
    const pet = placementPets.get(placement.id);
    if (!pet) {
      return;
    }
    // Same event the Dashboard fires, so 'Pet Viewed' stays whole across the portal's entry points.
    trackPetViewed(!!pet.chip, !!pet.device, 'foster', pet.species ?? undefined);
    setPlacementPet(pet);
  }

  /**
   * Drop every roster overlay. Used when the page navigates under them: a pet profile's device card
   * links back with a search on it, and the row it lands on would otherwise sit behind the modal
   * that opened it.
   */
  const dismissOverlays = useCallback(() => {
    setPlacementPet(null);
    closeDetail();
  }, [closeDetail]);

  const closePlacementPet = useCallback(() => setPlacementPet(null), []);

  return {
    homes,
    detailHome,
    detailOpen,
    openDetail,
    closeDetail,
    addOpen,
    setAddOpen,
    placementPet,
    closePlacementPet,
    viewablePlacementIds,
    viewPlacementPet,
    dismissOverlays,
  };
}
